<template>
  <div class="min-h-screen bg-base-100">
    <div class="container mx-auto px-4 py-12">
      <div class="max-w-4xl mx-auto">
        <h1 class="text-4xl font-bold text-base-content mb-8">
          Declaración de Accesibilidad
        </h1>

        <div class="prose prose-lg max-w-none">
          <p class="text-lg text-base-content/80 mb-8">
            DESERTANDO CLUB DE AVENTURAS, S.L. se compromete a hacer que su
            sitio web sea accesible para todas las personas, independientemente
            de sus capacidades o la tecnología que utilicen.
          </p>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              COMPROMISO CON LA ACCESIBILIDAD
            </h2>
            <p class="text-base-content/80 mb-4">
              Nos esforzamos por garantizar que nuestro sitio web sea accesible
              para personas con discapacidades. Trabajamos continuamente para
              mejorar la experiencia del usuario para todos y aplicamos los
              estándares de accesibilidad pertinentes.
            </p>
            <p class="text-base-content/80">
              Nuestro objetivo es cumplir con las Pautas de Accesibilidad para
              el Contenido Web (WCAG) 2.1 nivel AA, que son las pautas
              internacionales desarrolladas por el Consorcio World Wide Web
              (W3C).
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              ESTÁNDARES DE ACCESIBILIDAD
            </h2>
            <p class="text-base-content/80 mb-4">
              Este sitio web se esfuerza por cumplir con los siguientes
              estándares:
            </p>
            <ul class="list-disc pl-6 text-base-content/80 mb-4">
              <li>
                <strong>WCAG 2.1 Nivel AA:</strong> Pautas de Accesibilidad para
                el Contenido Web
              </li>
              <li>
                <strong>EN 301 549:</strong> Norma europea de accesibilidad
              </li>
              <li>
                <strong>Real Decreto 1112/2018:</strong> Normativa española
                sobre accesibilidad de sitios web del sector público
              </li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              MEDIDAS DE ACCESIBILIDAD IMPLEMENTADAS
            </h2>
            <div class="grid md:grid-cols-2 gap-6">
              <div class="bg-base-200 p-6 rounded-lg">
                <h3 class="text-xl font-semibold text-base-content mb-3">
                  🎯 Navegación y Estructura
                </h3>
                <ul class="list-disc pl-6 text-base-content/80">
                  <li>Navegación consistente en todas las páginas</li>
                  <li>Estructura de encabezados lógica y jerárquica</li>
                  <li>Enlaces descriptivos y contextuales</li>
                  <li>Breadcrumbs o migas de pan cuando sea apropiado</li>
                </ul>
              </div>

              <div class="bg-base-200 p-6 rounded-lg">
                <h3 class="text-xl font-semibold text-base-content mb-3">
                  🎨 Diseño Visual
                </h3>
                <ul class="list-disc pl-6 text-base-content/80">
                  <li>Contraste de color adecuado (mínimo 4.5:1)</li>
                  <li>
                    Texto redimensionable hasta 200% sin pérdida de
                    funcionalidad
                  </li>
                  <li>Información no dependiente únicamente del color</li>
                  <li>Diseño responsive para diferentes dispositivos</li>
                </ul>
              </div>

              <div class="bg-base-200 p-6 rounded-lg">
                <h3 class="text-xl font-semibold text-base-content mb-3">
                  ⌨️ Teclado y Interacción
                </h3>
                <ul class="list-disc pl-6 text-base-content/80">
                  <li>Navegación completa mediante teclado</li>
                  <li>Indicadores de foco visibles</li>
                  <li>Orden de tabulación lógico</li>
                  <li>Accesos directos cuando sea apropiado</li>
                </ul>
              </div>

              <div class="bg-base-200 p-6 rounded-lg">
                <h3 class="text-xl font-semibold text-base-content mb-3">
                  🔊 Contenido Multimedia
                </h3>
                <ul class="list-disc pl-6 text-base-content/80">
                  <li>Textos alternativos para imágenes</li>
                  <li>Transcripciones para audio cuando sea posible</li>
                  <li>Subtítulos para videos cuando estén disponibles</li>
                  <li>Controles de reproducción accesibles</li>
                </ul>
              </div>
            </div>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              TECNOLOGÍAS COMPATIBLES
            </h2>
            <p class="text-base-content/80 mb-4">
              Este sitio web está diseñado para ser compatible con las
              siguientes tecnologías de asistencia:
            </p>
            <ul class="list-disc pl-6 text-base-content/80 mb-4">
              <li>Lectores de pantalla (NVDA, JAWS, VoiceOver, TalkBack)</li>
              <li>Software de reconocimiento de voz</li>
              <li>Navegación mediante teclado</li>
              <li>Magnificadores de pantalla</li>
              <li>Navegadores con funciones de accesibilidad activadas</li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              LIMITACIONES CONOCIDAS
            </h2>
            <p class="text-base-content/80 mb-4">
              A pesar de nuestros esfuerzos, es posible que algunas partes del
              sitio web no sean completamente accesibles. Estamos trabajando
              para identificar y solucionar estas limitaciones:
            </p>
            <ul class="list-disc pl-6 text-base-content/80 mb-4">
              <li>
                Algunos contenidos de terceros pueden no cumplir completamente
                con los estándares de accesibilidad
              </li>
              <li>
                Documentos PDF antiguos pueden no ser completamente accesibles
              </li>
              <li>
                Algunas funcionalidades interactivas complejas pueden requerir
                tecnologías específicas
              </li>
            </ul>
            <div class="bg-info/10 border-l-4 border-info p-4">
              <p class="text-base-content/80">
                <strong>Nota:</strong> Trabajamos continuamente para mejorar la
                accesibilidad de nuestro sitio web. Si encuentra algún problema,
                por favor contáctenos.
              </p>
            </div>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              EVALUACIÓN Y PRUEBAS
            </h2>
            <p class="text-base-content/80 mb-4">
              La accesibilidad de este sitio web ha sido evaluada mediante:
            </p>
            <ul class="list-disc pl-6 text-base-content/80 mb-4">
              <li>Evaluación automática con herramientas especializadas</li>
              <li>Revisión manual del código y contenido</li>
              <li>Pruebas con tecnologías de asistencia</li>
              <li>Evaluación continua durante el desarrollo</li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              FEEDBACK Y CONTACTO
            </h2>
            <p class="text-base-content/80 mb-4">
              Valoramos sus comentarios sobre la accesibilidad de nuestro sitio
              web. Si encuentra alguna barrera de accesibilidad o tiene
              sugerencias para mejorar, por favor contáctenos:
            </p>
            <div class="bg-base-200 p-6 rounded-lg mb-4">
              <h3 class="text-xl font-semibold text-base-content mb-3">
                Información de Contacto para Accesibilidad
              </h3>
              <p class="text-base-content/80 mb-2">
                <strong>Email:</strong>
                <a
                  href="mailto:info@soleando.eu"
                  class="text-primary hover:underline"
                  >info@soleando.eu</a
                >
              </p>
              <p class="text-base-content/80 mb-2">
                <strong>Teléfono:</strong> (+34) 608 76 29 62 / (+34) 620 50 17
                61
              </p>
              <p class="text-base-content/80 mb-2">
                <strong>Dirección:</strong> C/ Arturo Soria, 333 – 28033 Madrid,
                España
              </p>
              <p class="text-base-content/80">
                <strong>Asunto sugerido:</strong> "Accesibilidad Web -
                [Descripción del problema]"
              </p>
            </div>
            <p class="text-base-content/80">
              Nos comprometemos a responder a todas las consultas sobre
              accesibilidad en un plazo de 5 días laborables y a trabajar para
              resolver cualquier problema identificado.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              PROCESO DE RECLAMACIÓN
            </h2>
            <p class="text-base-content/80 mb-4">
              Si no está satisfecho con nuestra respuesta a su consulta sobre
              accesibilidad, puede:
            </p>
            <ol class="list-decimal pl-6 text-base-content/80 mb-4">
              <li class="mb-2">
                Contactar con nuestro responsable de accesibilidad
              </li>
              <li class="mb-2">Presentar una reclamación formal por escrito</li>
              <li>Contactar con las autoridades competentes si es necesario</li>
            </ol>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-base-content mb-4">
              ACTUALIZACIONES DE ESTA DECLARACIÓN
            </h2>
            <p class="text-base-content/80">
              Esta declaración de accesibilidad se revisa y actualiza
              regularmente para reflejar las mejoras continuas en la
              accesibilidad de nuestro sitio web.
            </p>
          </section>

          <div class="text-center pt-8 border-t border-base-300">
            <p class="text-sm text-base-content/60 mb-2">
              <strong>Fecha de esta declaración:</strong> Enero 2025
            </p>
            <p class="text-sm text-base-content/60">
              <strong>Última revisión:</strong> Enero 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
definePageMeta({
  layout: "default",
});

useSeoMeta({
  title: "Declaración de Accesibilidad - Soleando",
  description:
    "Declaración de accesibilidad de Soleando. Compromiso con la accesibilidad web para todas las personas.",
});
</script>

<style scoped></style>
